import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, ImagePlus, X } from "lucide-react";
import { getReviewRequest, submitReview, uploadReviewPhoto, fileUrl } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ReviewPage() {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [suburb, setSuburb] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const MAX_PHOTOS = 5;

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`You can add up to ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    for (const file of files.slice(0, room)) {
      try {
        const res = await uploadReviewPhoto(token, file);
        setPhotos((list) => [...list, res.data.storage_path]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  useEffect(() => {
    getReviewRequest(token)
      .then((r) => setRequest(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This review link is not valid"));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (comment.trim().length < 10) {
      toast.error("Please write a sentence or two so it's useful to other guests.");
      return;
    }
    setBusy(true);
    try {
      await submitReview(token, { rating, comment, guest_suburb: suburb, photos });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-[760px] mx-auto px-6 py-20" data-testid="review-page">
      <Seo
        title="PARADISIO RESORTS | Leave a review"
        description="Share your feedback on your stay with PARADISIO RESORTS."
        noindex
      />
      {error && (
        <div data-testid="review-error">
          <h1 className="font-display text-4xl text-[#0F2A52]">{error}</h1>
          <Link to="/" className="inline-block mt-8 text-sm text-[#B08A3E]">
            Back to PARADISIO RESORTS
          </Link>
        </div>
      )}

      {!error && !request && <p className="text-sm text-[#4A4A4A]">Loading…</p>}

      {request && done && (
        <div data-testid="review-success">
          <h1 className="font-display text-4xl sm:text-5xl text-[#0F2A52]">Thank you</h1>
          <p className="text-base text-[#4A4A4A] leading-relaxed mt-6">
            Your review has been sent to Robert. Once he publishes it, it will appear on the
            {" "}{request.property_name} page.
          </p>
          <Link to="/" className="inline-block mt-8 text-sm text-[#B08A3E]">
            Back to PARADISIO RESORTS
          </Link>
        </div>
      )}

      {request && !done && (
        <form onSubmit={onSubmit} data-testid="review-form">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#B08A3E]">Your stay</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0F2A52] mt-5 leading-[1.05]">
            How was {request.property_name}?
          </h1>
          <p className="text-base text-[#4A4A4A] leading-relaxed mt-6">
            Hi {request.guest_name}, thanks for staying with us. Only your first name and suburb are shown.
          </p>

          <div className="flex gap-2 mt-10" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                aria-label={`${n} star`}
                data-testid={`review-star-${n}`}
                className="p-1"
              >
                <Star
                  size={30}
                  className={n <= rating ? "fill-[#B08A3E] text-[#B08A3E]" : "text-[#0F2A52]/25"}
                />
              </button>
            ))}
          </div>

          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mt-10 mb-2">
            Your review
          </label>
          <textarea
            rows={6}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full rounded-none border-0 border-b border-[#0F2A52]/25 bg-transparent px-0 py-3 text-sm resize-none focus:outline-none focus:border-[#0F2A52]"
            placeholder="What did you enjoy? Anything future guests should know?"
            data-testid="review-comment"
          />

          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mt-8 mb-2">
            Suburb or town (optional)
          </label>
          <input
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            className="w-full rounded-none border-0 border-b border-[#0F2A52]/25 bg-transparent px-0 py-3 text-sm focus:outline-none focus:border-[#0F2A52]"
            data-testid="review-suburb"
          />

          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mt-10 mb-2">
            Add your photos (optional)
          </label>
          <p className="text-sm text-[#4A4A4A]">
            Up to {MAX_PHOTOS} photos from your stay — beach, sunsets, the view from the deck. Robert will check
            them before any appear on the website, credited to your first name.
          </p>

          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-5" data-testid="review-photo-previews">
              {photos.map((p) => (
                <div key={p} className="relative aspect-square overflow-hidden">
                  <img src={fileUrl(p)} alt="Your upload" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((list) => list.filter((x) => x !== p))}
                    aria-label="Remove photo"
                    data-testid={`review-photo-remove-${photos.indexOf(p)}`}
                    className="absolute top-1.5 right-1.5 bg-white/90 p-1 rounded-full"
                  >
                    <X size={13} className="text-[#0F2A52]" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {photos.length < MAX_PHOTOS && (
            <label
              className="inline-flex items-center gap-2.5 mt-5 px-5 py-3 border border-[#0F2A52]/25 text-sm text-[#0F2A52] cursor-pointer hover:border-[#0F2A52] transition-colors"
              data-testid="review-photo-picker"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              {uploading ? "Uploading…" : "Choose photos"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onPickPhotos}
                disabled={uploading}
                data-testid="review-photo-input"
              />
            </label>
          )}

          <button
            type="submit"
            disabled={busy || uploading}
            className="inline-flex items-center gap-3 bg-[#0F2A52] text-[#FCFAF6] px-8 py-4 text-sm mt-10 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 disabled:opacity-60"
            data-testid="review-submit"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Sending" : "Send review"}
          </button>
        </form>
      )}
    </main>
  );
}
